import { Box, List, ListItemButton, ListItemText, ListSubheader, Paper } from '@mui/material';
import React from 'react';
import type { DealValues } from '../../api/registryDealTypes';
import { GROUP_LABELS, type SectionGroup } from '../../domain/setup/fieldTypes';
import { type DefaultsSection, isNotSet, shownKeys } from './liveDefaultSections';

interface Props {
  sections: DefaultsSection[];
  current: string;
  draft: DealValues;
  changed: string[];
  errors: Record<string, string>;
  onSelect: (sectionId: string) => void;
}

export default function DefaultsSideNav({
  sections,
  current,
  draft,
  changed,
  errors,
  onSelect,
}: Props) {
  const groups = [...new Set(sections.map((section) => section.group))] as SectionGroup[];
  return (
    <Paper variant="outlined" sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
      <List dense disablePadding aria-label="Default value sections">
        {groups.map((group) => (
          <React.Fragment key={group}>
            <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent', fontWeight: 700 }}>
              {GROUP_LABELS[group]}
            </ListSubheader>
            {sections
              .filter((section) => section.group === group)
              .map((section) => {
                const keys = shownKeys(section, draft);
                const notSet = keys.filter((key) => isNotSet(draft[key])).length;
                const invalid = keys.some((key) => errors[key]);
                const edited = keys.some((key) => changed.includes(key));
                return (
                  <ListItemButton
                    key={section.id}
                    selected={section.id === current}
                    onClick={() => onSelect(section.id)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={
                        <>
                          {section.title}
                          {notSet > 0 && (
                            <Box
                              component="span"
                              aria-label={`${notSet} not set`}
                              sx={{ ml: 0.5, color: 'error.main' }}
                            >
                              ({notSet})
                            </Box>
                          )}
                          {(edited || invalid) && (
                            <Box
                              component="span"
                              aria-label={invalid ? 'values to fix' : 'unsaved changes'}
                              sx={{
                                ml: 0.75,
                                fontWeight: 700,
                                color: invalid ? 'error.main' : 'primary.main',
                              }}
                            >
                              •
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
}
